import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import {
  agentUsageService,
  type StatsFollowUp,
  type StatsGrain,
  type StatsPoint,
  type StatsScope,
} from '../services/api'

const ZONE = 'Europe/Paris'

const COLUMNS = [
  { id: 'models', label: 'Modèles', color: '#8a7340' },
  { id: 'instances', label: 'Instances', color: '#4f6f8f' },
  { id: 'calls', label: 'Appels', color: '#c47b3a' },
  { id: 'inputTokens', label: 'Entrée', color: '#6b7c4a' },
  { id: 'outputTokens', label: 'Sortie', color: '#8b5e3c' },
  { id: 'cacheTokens', label: 'Cache', color: '#7a6a8a' },
  { id: 'totalTokens', label: 'Total', color: '#3d4f5f' },
  { id: 'secrets', label: 'Secrets', color: '#6a7a78' },
] as const

type ColumnId = (typeof COLUMNS)[number]['id']

const GRAINS: Array<{ id: StatsGrain; label: string }> = [
  { id: 'year', label: 'Année' },
  { id: 'month', label: 'Mois' },
  { id: 'week', label: 'Semaines' },
  { id: 'day', label: 'Jours' },
  { id: 'hour', label: 'Heures' },
]

const DEFAULT_COLUMNS = COLUMNS.map((column) => column.id).join(',')

/**
 * * Table and chart share the query string, so switching tabs keeps every filter.
 */
export function FollowUp({ presentation }: { presentation: 'table' | 'chart' }) {
  const [params, setParams] = useSearchParams()
  const grain = parseGrain(params.get('grain'))
  const cursor = params.get('cursor') || todayCursor()
  const scope = parseScope(params.get('scope'))
  const teamId = params.get('team') || ''
  const agentName = params.get('agent') || ''
  const visible = parseColumns(params.get('cols'))

  const query = useQuery<StatsFollowUp, Error>(
    ['suivi', grain, cursor, scope, teamId, agentName],
    () => agentUsageService.getStats({
      grain,
      cursor,
      scope,
      teamId: scope === 'team' ? teamId : undefined,
      agentName: scope === 'agent' ? agentName : undefined,
    }),
    { retry: 1, keepPreviousData: true },
  )

  const rows = query.data?.rows ?? []
  const rangeLabel = query.data ? formatRange(grain, query.data.start, query.data.end) : ''

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(patch)) {
      if (key === 'cols') next.set(key, value)
      else if (value) next.set(key, value)
      else next.delete(key)
    }
    setParams(next, { replace: true })
  }

  const shift = (delta: number) => {
    set({ cursor: shiftCursor(grain, cursor, delta) })
  }

  const toggleColumn = (id: ColumnId) => {
    const next = visible.includes(id) ? visible.filter((item) => item !== id) : [...visible, id]
    const ordered = COLUMNS.map((column) => column.id).filter((columnId) => next.includes(columnId))
    set({ cols: ordered.join(',') })
  }

  const shown = COLUMNS.filter((column) => visible.includes(column.id))
  const plotted = shown.filter((column) => column.id !== 'cacheTokens')
  const agentNames = useMemo(() => {
    const names = new Set((query.data?.agents ?? []).map((agent) => agent.name).filter(Boolean))
    return [...names].sort((left, right) => left.localeCompare(right, 'fr'))
  }, [query.data?.agents])

  return (
    <div className="page-content relative min-h-screen">
      <div className="background-image">
        <img src="/logo.jpg" alt="" />
      </div>
      <div className="content-wrapper space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="text-3xl font-extrabold tracking-tight tab_title">
            {presentation === 'table' ? 'Statistiques' : 'Graphiques'}
          </h1>
          <button type="button" className="button button--secondary flex items-center gap-2" onClick={() => exportCsv(grain, cursor, rows, shown)}>
            <Download className="w-4 h-4" />
            <span>Exporter CSV</span>
          </button>
        </div>

        <div className="card">
          <div className="px-4 py-4 flex flex-wrap items-end gap-4">
            <div>
              <p className="text-sm text-secondary mb-2">Plage</p>
              <div className="flex flex-wrap gap-1" role="group" aria-label="Granularité">
                {GRAINS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`button ${grain === item.id ? 'button--primary' : 'button--secondary'}`}
                    onClick={() => set({ grain: item.id })}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" className="p-2 rounded-lg bg-gray-100 text-primary" title="Plage précédente" onClick={() => shift(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-3 py-2 text-sm font-medium text-primary bg-gray-100 rounded-lg capitalize min-w-40 text-center">
                {rangeLabel || '…'}
              </span>
              <button type="button" className="p-2 rounded-lg bg-gray-100 text-primary" title="Plage suivante" onClick={() => shift(1)}>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <label className="text-sm text-secondary" htmlFor="suivi-scope">
              Cible
              <select
                id="suivi-scope"
                name="suivi-scope"
                className="form-input mt-1 block"
                value={scope}
                onChange={(event) => set({ scope: event.target.value, team: '', agent: '' })}
              >
                <option value="all">Tout le serveur</option>
                <option value="team">Une équipe</option>
                <option value="agent">Un nom d'agent</option>
              </select>
            </label>
            {scope === 'team' && (
              <label className="text-sm text-secondary" htmlFor="suivi-team">
                Équipe
                <select
                  id="suivi-team"
                  name="suivi-team"
                  className="form-input mt-1 block"
                  value={teamId}
                  onChange={(event) => set({ team: event.target.value })}
                >
                  <option value="">Choisir…</option>
                  {(query.data?.teams ?? []).map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </label>
            )}
            {scope === 'agent' && (
              <label className="text-sm text-secondary" htmlFor="suivi-agent">
                Agent
                <select
                  id="suivi-agent"
                  name="suivi-agent"
                  className="form-input mt-1 block"
                  value={agentName}
                  onChange={(event) => set({ agent: event.target.value })}
                >
                  <option value="">Choisir…</option>
                  {agentNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>

        {query.error ? <p className="text-sm text-error">{query.error.message}</p> : null}

        <div className="flex flex-col lg:flex-row gap-4 items-start">
          <div className="card flex-1 min-w-0 w-full overflow-hidden">
            {query.isLoading ? <p className="px-6 py-8 text-sm text-secondary">Chargement…</p> : null}
            {!query.isLoading && presentation === 'table' && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-secondary font-medium">Période</th>
                      {shown.map((column) => (
                        <th key={column.id} className="text-right px-4 py-3 text-secondary font-medium" title={column.id === 'cacheTokens' ? 'Les moteurs ne remontent pas encore le cache.' : undefined}>
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.bucketStart} className="border-t border-gray-200">
                        <td className="px-4 py-3 text-primary">{formatBucket(grain, row)}</td>
                        {shown.map((column) => (
                          <td key={column.id} className="px-4 py-3 text-right text-primary">
                            {formatCell(row[column.id])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!query.isLoading && presentation === 'chart' && (
              <div className="suivi-chart px-2 py-4" style={{ width: '100%', height: 380 }}>
                {plotted.length === 0 ? (
                  <p className="px-4 text-sm text-secondary">Cochez au moins un chiffre dans la légende.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rows.map((row) => ({ label: formatBucket(grain, row), ...chartValues(row, plotted) }))}>
                      <CartesianGrid stroke="#e4ddd0" />
                      <XAxis dataKey="label" tick={{ fill: '#5c564c', fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: '#5c564c', fontSize: 12 }} />
                      <Tooltip />
                      {plotted.map((column) => (
                        <Line key={column.id} type="monotone" dataKey={column.id} name={column.label} stroke={column.color} dot={false} strokeWidth={2} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            )}
          </div>

          <aside className="card w-full lg:w-56 shrink-0">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-primary">Légende</h2>
            </div>
            <ul className="px-4 py-3 space-y-2">
              {COLUMNS.map((column) => (
                <li key={column.id}>
                  <label className="flex items-center gap-2 text-sm text-primary" htmlFor={`suivi-col-${column.id}`}>
                    <input
                      id={`suivi-col-${column.id}`}
                      name={`suivi-col-${column.id}`}
                      type="checkbox"
                      checked={visible.includes(column.id)}
                      onChange={() => toggleColumn(column.id)}
                    />
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: column.color }} />
                    <span>{column.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </div>
    </div>
  )
}

function parseGrain(value: string | null): StatsGrain {
  if (value === 'hour' || value === 'day' || value === 'week' || value === 'month' || value === 'year') return value
  return 'day'
}

function parseScope(value: string | null): StatsScope {
  if (value === 'team' || value === 'agent') return value
  return 'all'
}

function parseColumns(value: string | null): ColumnId[] {
  const known = new Set<string>(COLUMNS.map((column) => column.id))
  const raw = (value ?? DEFAULT_COLUMNS).split(',').filter((item) => known.has(item))
  return raw as ColumnId[]
}

function todayCursor(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
}

function shiftCursor(grain: StatsGrain, cursor: string, delta: number): string {
  const [year, month, day] = cursor.split('-').map(Number)
  if (!year || !month || !day) return todayCursor()
  if (grain === 'hour') {
    const date = new Date(Date.UTC(year, month - 1, day + delta))
    return isoDay(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
  }
  if (grain === 'day' || grain === 'week') {
    const date = new Date(Date.UTC(year, month - 1 + delta, 1))
    return isoDay(date.getUTCFullYear(), date.getUTCMonth() + 1, 1)
  }
  return isoDay(year + delta, 1, 1)
}

function isoDay(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function formatBucket(grain: StatsGrain, row: StatsPoint): string {
  const start = new Date(row.bucketStart)
  const end = new Date(new Date(row.bucketEnd).getTime() - 1)
  if (grain === 'hour') {
    return start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: ZONE })
  }
  if (grain === 'week') {
    const left = start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: ZONE })
    const right = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: ZONE })
    return `${left} – ${right}`
  }
  if (grain === 'month') {
    return start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: ZONE })
  }
  if (grain === 'year') {
    return start.toLocaleDateString('fr-FR', { year: 'numeric', timeZone: ZONE })
  }
  return start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: ZONE })
}

function formatRange(grain: StatsGrain, startIso: string, endIso: string): string {
  const start = new Date(startIso)
  const last = new Date(new Date(endIso).getTime() - 1)
  if (grain === 'hour') {
    return start.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: ZONE })
  }
  if (grain === 'day' || grain === 'week') {
    return start.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: ZONE })
  }
  if (grain === 'month') {
    return start.toLocaleDateString('fr-FR', { year: 'numeric', timeZone: ZONE })
  }
  const left = start.toLocaleDateString('fr-FR', { year: 'numeric', timeZone: ZONE })
  const right = last.toLocaleDateString('fr-FR', { year: 'numeric', timeZone: ZONE })
  return `${left} – ${right}`
}

function formatCell(value: number | null): string {
  if (value === null) return '—'
  return value.toLocaleString('fr-FR')
}

function chartValues(row: StatsPoint, shown: ReadonlyArray<(typeof COLUMNS)[number]>): Record<string, number | null> {
  const values: Record<string, number | null> = {}
  for (const column of shown) {
    values[column.id] = row[column.id]
  }
  return values
}

function exportCsv(
  grain: StatsGrain,
  cursor: string,
  rows: StatsPoint[],
  shown: ReadonlyArray<(typeof COLUMNS)[number]>,
) {
  const header = ['Période', ...shown.map((column) => column.label)].join(';')
  const lines = rows.map((row) => {
    const cells = shown.map((column) => {
      const value = row[column.id]
      return value === null ? '' : String(value)
    })
    return [formatBucket(grain, row), ...cells].join(';')
  })
  const blob = new Blob([`\uFEFF${header}\n${lines.join('\n')}\n`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `suivi-${cursor}-${grain}.csv`
  link.click()
  URL.revokeObjectURL(url)
}
